// src/pages/OrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { orderService } from '../services/orderService';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login', { replace: true });
      return;
    }

    const fetchOrders = async () => {
      try {
        const data = await orderService.getMyOrders();
        // Sort orders by newest first
        const sortedData = (data || []).sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
        setOrders(sortedData);
      } catch (err) {
        console.error("Failed to fetch orders", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isLoggedIn, navigate]);

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DELIVERED': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'CANCELLED': return 'text-red-600 bg-red-50 border-red-200';
      case 'PROCESSING': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-orange-600 bg-orange-50 border-orange-200'; // PENDING etc.
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  return (
    <div className="py-6 px-4 md:px-8 max-w-5xl mx-auto w-full min-h-[70vh]">
      <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-5">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors">
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1 className="text-2xl md:text-3xl font-black text-[#0F2F63] flex items-center gap-3">
          <i className="fas fa-box-open text-[#FF7A59]"></i> My Orders
        </h1>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <i className="fas fa-spinner fa-spin text-4xl text-[#0F2F63] mb-4"></i>
          <p className="text-gray-500 font-medium">Fetching your orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm">
          <div className="w-24 h-24 bg-gray-50 text-gray-300 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <i className="fas fa-shopping-bag text-4xl"></i>
          </div>
          <h3 className="text-2xl text-gray-800 font-black mb-2">No Orders Yet</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-8">You haven't placed any orders. Start exploring our products!</p>
          <Link to="/quick" className="bg-[#0F2F63] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#1a4a8a] transition-all shadow-md inline-block">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          {orders.map((order) => (
            <div key={order.globalOrderId} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 md:p-6">
              
              <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 border-b border-gray-50 pb-4 mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-sm md:text-base font-black text-gray-900">Order #{order.globalOrderId}</h3>
                    <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest border ${getStatusColor(order.paymentStatus)}`}>
                      {order.paymentStatus}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 font-semibold">{formatDate(order.orderedAt)}</p>
                </div>
                <div className="text-left md:text-right">
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">Total Amount</p>
                  {/* FIXED: Removed Decimals */}
                  <p className="text-xl font-black text-[#0F2F63]">₹ {Math.round(order.payable)}</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex gap-6 text-sm">
                  <div>
                    <p className="text-gray-400 font-semibold text-xs mb-0.5">Items</p>
                    <p className="font-bold text-gray-800">{order.totalItems}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-semibold text-xs mb-0.5">Payment</p>
                    <p className="font-bold text-gray-800">{order.paymentType}</p>
                  </div>
                  
                  {order.totalTax > 0 && (
                    <div>
                      <p className="text-gray-400 font-semibold text-xs mb-0.5">Tax</p>
                      {/* FIXED: Removed Decimals */}
                      <p className="font-bold text-gray-800">₹ {Math.round(order.totalTax)}</p>
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => navigate(`/orders/${order.globalOrderId}`)}
                  className="w-full sm:w-auto px-6 py-2.5 bg-sky-50 hover:bg-sky-100 text-sky-600 rounded-xl font-bold text-sm transition-colors border border-sky-100 flex items-center justify-center gap-2"
                >
                  View Details <i className="fas fa-chevron-right text-xs"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;